import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AM } from "@/constants/testIds";
import { createAllergenMaster, updateAllergenMaster } from "@/lib/allergenMasterApi";
import { ALLERGEN_TYPES } from "@/lib/allergyLookup";

const TYPE_OPTIONS = ALLERGEN_TYPES.filter((row) => row.id);

const BLANK = {
  displayName: "",
  allergenType: "drug-ingredient",
  codeSystem: "",
  code: "",
  codeDisplay: "",
  rxnormCode: "",
  snomedCode: "",
  synonyms: "",
  active: true,
};

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to save allergen.";
}

export default function AllergenMasterForm({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        displayName: editing.displayName || "",
        allergenType: editing.allergenType || "drug-ingredient",
        codeSystem: editing.codeSystem || "",
        code: editing.code || "",
        codeDisplay: editing.codeDisplay || "",
        rxnormCode: editing.rxnormCode || "",
        snomedCode: editing.snomedCode || "",
        synonyms: (editing.synonyms || []).join(", "),
        active: editing.active !== false,
      });
    } else {
      setForm(BLANK);
    }
  }, [open, editing]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.displayName.trim()) {
      toast.error("Display name is required.");
      return;
    }
    if (!form.allergenType) {
      toast.error("Allergen type is required.");
      return;
    }
    const code = form.code.trim();
    const codeSystem = form.codeSystem.trim();
    if (code && !codeSystem) {
      toast.error("Code system is required when a code is set.");
      return;
    }
    if (codeSystem && !code) {
      toast.error("Code is required when a code system is set.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        allergenType: form.allergenType,
        synonyms: form.synonyms
          .split(",")
          .map((row) => row.trim())
          .filter(Boolean),
        active: form.active,
      };
      if (code && codeSystem) {
        payload.code = code;
        payload.codeSystem = codeSystem;
        if (form.codeDisplay.trim()) payload.codeDisplay = form.codeDisplay.trim();
      }
      if (form.rxnormCode.trim()) payload.rxnormCode = form.rxnormCode.trim();
      if (form.snomedCode.trim()) payload.snomedCode = form.snomedCode.trim();
      if (editing?.additionalCodings?.length) {
        payload.additionalCodings = editing.additionalCodings;
      }
      if (editing) {
        await updateAllergenMaster(editing.id, payload);
        toast.success("Allergen updated. Existing patient allergy records were not changed.");
      } else {
        await createAllergenMaster(payload);
        toast.success("Allergen added to the master catalog.");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={AM.form}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit allergen" : "Add allergen"}</DialogTitle>
          <DialogDescription>
            Allergen Master terminology only. Patient allergy records keep their own snapshot
            and are not rewritten when this row changes.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={AM.formDisplayName}>Display name *</Label>
            <Input
              id={AM.formDisplayName}
              data-testid={AM.formDisplayName}
              value={form.displayName}
              onChange={(event) => set("displayName")(event.target.value)}
              placeholder="Penicillin"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={AM.formAllergenType}>Allergen type *</Label>
            <Select value={form.allergenType} onValueChange={set("allergenType")}>
              <SelectTrigger id={AM.formAllergenType} data-testid={AM.formAllergenType}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((row) => (
                  <SelectItem key={row.id} value={row.id}>
                    {row.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                data-testid={AM.formActive}
                checked={form.active}
                onCheckedChange={set("active")}
              />
              Active
            </label>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={AM.formSynonyms}>Synonyms</Label>
            <Input
              id={AM.formSynonyms}
              data-testid={AM.formSynonyms}
              value={form.synonyms}
              onChange={(event) => set("synonyms")(event.target.value)}
              placeholder="PCN, penicillin class"
            />
            <p className="text-xs text-slate-500">Comma-separated. Used by Allergy Lookup.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={AM.formCodeSystem}>Code system</Label>
            <Input
              id={AM.formCodeSystem}
              data-testid={AM.formCodeSystem}
              value={form.codeSystem}
              onChange={(event) => set("codeSystem")(event.target.value)}
              placeholder="http://snomed.info/sct"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={AM.formCode}>Code</Label>
            <Input
              id={AM.formCode}
              data-testid={AM.formCode}
              value={form.code}
              onChange={(event) => set("code")(event.target.value)}
              placeholder="Verified terminology code only"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={AM.formCodeDisplay}>Code display</Label>
            <Input
              id={AM.formCodeDisplay}
              data-testid={AM.formCodeDisplay}
              value={form.codeDisplay}
              onChange={(event) => set("codeDisplay")(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={AM.formRxnorm}>RxNorm code</Label>
            <Input
              id={AM.formRxnorm}
              data-testid={AM.formRxnorm}
              value={form.rxnormCode}
              onChange={(event) => set("rxnormCode")(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={AM.formSnomed}>SNOMED CT code</Label>
            <Input
              id={AM.formSnomed}
              data-testid={AM.formSnomed}
              value={form.snomedCode}
              onChange={(event) => set("snomedCode")(event.target.value)}
            />
          </div>
        </div>

        <p className="text-xs text-slate-500">
          Do not invent SNOMED CT or RxNorm codes. Leave code fields blank unless a verified
          identifier is already known.
        </p>

        {editing ? (
          <p className="text-xs text-slate-500">
            Internal id: <span className="font-fhir">{editing.id}</span>
            {editing._provenance?.activity ? (
              <>
                {" "}
                · Last {editing._provenance.activity} by{" "}
                {editing._provenance.agent?.display || editing._provenance.agent?.who}
              </>
            ) : null}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" data-testid={AM.formCancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" data-testid={AM.formSave} onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
